import {
    Box,
    Card,
    CardContent,
    Divider,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import GavelIcon from '@mui/icons-material/Gavel';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HistoryIcon from '@mui/icons-material/History';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatEUR } from '../../../constants/tool.fn';
import i18n from '../../../i18n';
import LinechartOfBusinessData, {
    createHistoryBusinessLineApexOptions,
} from '../../../component/charts/line-chart-of-business-data';
import CommonInput from '../../../component/form/common-input.component';
import PieChartOfBusinessData from '../../../component/charts/pie-chart-of-business-data';
import PeriodDateFilter from '../../../component/period-date-filter.component';
import { HistoryPeriod } from '../../../models/abstract/history-period.entity';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import dataAnalysisStore from '../data-analysis-state';
import { ErrorMessageBox } from '../data-analysis.page';
import HistoryDataStore, { HistoryCartogramMetric } from './history-data-state';

/**
 * 折线图 `ReactApexChart` 的 height，须与 `component/charts/line-chart-of-business-data.tsx` 中 `height` 保持同步
 */
const LINE_CHART_APEX_HEIGHT = 350;
/**
 * 折线图白卡最小高度，与 Figma 节点 `297:13900`（折线图外壳 h≈358px）一致。
 */
const LINE_CHART_CARD_MIN_HEIGHT_PX = 358;
/**
 * 折线图 CardContent 内边距，与 Figma `p-[12.174px]` 对齐（约 12px）。
 */
const LINE_CHART_CARD_CONTENT_PADDING_PX = 12;
/**
 * 饼图外层的 `minWidth` / `maxWidth`，须与 `pie-chart-of-business-data.tsx` 根节点 `Box` 一致
 */
const PIE_CHART_BOX_MIN_W = 250;
const PIE_CHART_BOX_MAX_W = 460;
/** 饼图无显式 height 时 Apex 常见可视高度约 200，占位与之对齐 */
const PIE_CHART_SKELETON_CIRCLE = 300;

/**
 * 卡片内单条 KPI 配置
 * - key: HistoryPeriod 字段名
 * - label: 显示文案
 * - isPrice: 是否格式化为欧元价格
 * - icon: 对应 Figma 图标（可选，无图标的条目传 null）
 * - isErrorValue: 数值是否用错误色（红色）显示
 */
type CardItem = {
    key: keyof HistoryPeriod;
    label: string;
    isPrice?: boolean;
    icon: JSX.Element | null;
    isErrorValue?: boolean;
};

const HistoryDataPage = observer(() => {
    const { t } = useTranslation();
    const theme = useTheme();
    const historyDataStore = useLocalObservable(() => new HistoryDataStore());
    /** 从主页面状态取当前餐厅，不依赖「当前营业状态」页是否打开 */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const stringId = mainPageData.currentRestaurant?.string_id || '';
    const startDate = dataAnalysisStore.startDate;
    const endDate = dataAnalysisStore.endDate;

    /** 历史经营「上月 / 本期」折线系列 */
    const historyBusinessLineSeries = useMemo(
        () => [
            {
                name: i18n.t('business_analysis_page_yoy_last_month'),
                data: historyDataStore.chartData.lastMonthData,
            },
            {
                name: i18n.t('business_analysis_page_current_business_data'),
                data: historyDataStore.chartData.thisMonthData,
            },
        ],
        [historyDataStore.chartData, i18n.language],
    );

    /** 历史经营折线图 Apex options（标题、两位小数等在本页组装） */
    const historyBusinessLineApexOptions = useMemo(
        () =>
            createHistoryBusinessLineApexOptions(theme, {
                rawXLabels: historyDataStore.chartConfig.XAxisText,
                height: LINE_CHART_APEX_HEIGHT,
                titleText: i18n.t('business_analysis_page_business_data_chart'),
                seriesCount: 2,
            }),
        [
            theme,
            historyDataStore.chartConfig.XAxisText,
            historyDataStore.chartData.lastMonthData,
            historyDataStore.chartData.thisMonthData,
            i18n.language,
        ],
    );

    /**
     * KPI 数据项列表，顺序与图标严格按 Figma 设计还原：
     * assignment→订单/桌台总数，MonetizationOn→实收/折扣，
     * FreeBreakfast→堂食，ShoppingBag→外卖，History→历史菜品，
     * ArrowUpward→催菜，HighlightOff(红)→退菜，Gavel→改价，
     * 无图标→反结账/小费/服务费
     */
    /** 普通条目图标颜色：深灰色，与 Figma 截图一致（--icon-neutral-800 = #1F2940） */
    const iconSx = { color: 'var(--icon-neutral-800)', fontSize: 24 };
    /** 红色图标（退菜相关）颜色 */
    const iconErrorSx = { color: 'var(--func-error)', fontSize: 24 };

    const cardArr: CardItem[] = [
        {
            key: 'table_total',
            label: i18n.t('business_analysis_page_text_order_table_total'),
            icon: <AssignmentIcon sx={iconSx} />,
        },
        {
            key: 'actual_amount_total',
            label: i18n.t('business_analysis_page_text_actual_amount_total'),
            isPrice: true,
            icon: <MonetizationOnIcon sx={iconSx} />,
        },
        {
            key: 'discount_amount_total',
            label: i18n.t('business_analysis_page_text_discount_amount_total'),
            isPrice: true,
            icon: <MonetizationOnIcon sx={iconSx} />,
        },
        {
            key: 'eat_in_people_total',
            label: i18n.t('business_analysis_page_text_eat_in_customer_total'),
            icon: <FreeBreakfastIcon sx={iconSx} />,
        },
        {
            key: 'eat_in_money_total',
            label: i18n.t('business_analysis_page_text_hall_amount_total'),
            isPrice: true,
            icon: <FreeBreakfastIcon sx={iconSx} />,
        },
        {
            key: 'take_out_total',
            label: i18n.t('business_analysis_page_text_takeout_total'),
            icon: <ShoppingBagIcon sx={iconSx} />,
        },
        {
            key: 'take_out_money_total',
            label: i18n.t('business_analysis_page_text_takeout_amount_total'),
            isPrice: true,
            icon: <ShoppingBagIcon sx={iconSx} />,
        },
        {
            key: 'dish_total',
            label: i18n.t('business_analysis_page_text_dish_total'),
            icon: <HistoryIcon sx={iconSx} />,
        },
        {
            key: 'rush_dish_count',
            label: i18n.t('business_analysis_page_text_rush_dish'),
            icon: <ArrowUpwardIcon sx={iconSx} />,
        },
        {
            key: 'cancel_dish_num',
            label: i18n.t('business_analysis_page_text_cancelled_dish'),
            icon: <HighlightOffIcon sx={iconErrorSx} />,
            isErrorValue: true,
        },
        {
            key: 'cancelled_amount_total',
            label: i18n.t('business_analysis_page_text_cancelled_amount_total'),
            isPrice: true,
            icon: <HighlightOffIcon sx={iconErrorSx} />,
            isErrorValue: true,
        },
        {
            key: 'reprice_num',
            label: i18n.t('business_analysis_page_text_reprice_num'),
            icon: <GavelIcon sx={iconSx} />,
            isErrorValue: true,
        },
        {
            key: 'reprice_amount',
            label: i18n.t('business_analysis_page_text_reprice_amount'),
            isPrice: true,
            icon: <GavelIcon sx={iconSx} />,
            isErrorValue: true,
        },
        {
            key: 'refunds_total',
            label: i18n.t('business_analysis_page_text_refunds_total'),
            icon: null,
            isErrorValue: true,
        },
        {
            key: 'total_tip_amount',
            label: i18n.t('business_analysis_page_text_tip'),
            isPrice: true,
            icon: null,
        },
        {
            key: 'total_service_fee',
            label: i18n.t('business_analysis_page_text_service_charges'),
            isPrice: true,
            icon: null,
        },
    ];

    /** 折线图指标下拉选项，value 与 history/cartogram 按日项字段名一致（非 history/period 汇总字段） */
    const selectList = [
        {
            value: 'actual_amount_total',
            label: i18n.t('business_analysis_page_text_actual_amount_total'),
        },
        {
            value: 'take_out_total',
            label: i18n.t('business_analysis_page_text_takeout_amount_total'),
        },
        {
            value: 'eat_in_total',
            label: i18n.t('business_analysis_page_text_hall_amount_total'),
        },
    ];

    useEffect(() => {
        historyDataStore.fetchData(stringId, startDate, endDate);
    }, [stringId, startDate, endDate]);

    /**
     * 卡片通用 sx：白底、圆角 16px、阴影，与 Figma 一致
     * 禁止 Hex 硬编码，全部使用 CSS 变量
     */
    const cardSx = {
        bgcolor: 'var(--fill-neutral-100)',
        boxShadow: '0px 0px 4px 0px var(--stroke-neutral-800-a25)',
        borderRadius: '16px',
    };

    /** 左侧 KPI 行内容高度，与 Figma 数据行 `h-[24px]` + Stack `spacing={2}`（16px）节奏一致 */
    const kpiRowMinHeight = 24;

    if (historyDataStore.loading) {
        return (
            <HistoryDataPageLoadingSkeleton
                cardSx={cardSx}
                kpiSkeletonRowCount={cardArr.length}
                kpiRowMinHeight={kpiRowMinHeight}
            />
        );
    }

    if (historyDataStore.errorMessage) {
        return <ErrorMessageBox errorMessage={historyDataStore.errorMessage} />;
    }

    return (
        <Stack spacing={3} sx={{ py: 2 }}>
            {/* ── 顶部行：页面标题 + 周期选择器 ─────────────────────── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Typography
                    variant="h4"
                    sx={{ color: 'var(--text-dark)', fontWeight: 700 }}
                >
                    {t('business_analysis_page_text_operate_info')}
                </Typography>

                {/* 周期 + 自定义日期 筛选（公共组件） */}
                <PeriodDateFilter
                    startDate={startDate}
                    endDate={endDate}
                    pickerValue={dataAnalysisStore.pickerValue}
                    onDateTimeChange={dataAnalysisStore.handleDateTime}
                />
            </Box>

            {/* ── 主体布局：≥md 左 1/3 + 右 2/3；xs 单列换行；右列内饼图 xs 单列、md 双列 ── */}
            <Box
                sx={{
                    display: 'grid',
                    gap: '24px',
                    alignItems: 'stretch',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 2fr)' },
                }}
            >
                {/* 左列 KPI 数据卡片（与右列同高） */}
                <Card
                    sx={{
                        ...cardSx,
                        width: '100%',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    <CardContent
                        sx={{
                            px: 4,
                            py: 6,
                            '&:last-child': { pb: 3 },
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/*
                         * 与 Figma「数据卡片」一致：行间分割线 + 纵向 gap 16px（theme.spacing(4)，spacing 基数为 4）。
                         * Divider 使用浅色描边 token，避免视觉上「粘成一块」。
                         */}
                        <Stack
                            divider={
                                <Divider
                                    flexItem
                                    sx={(theme) => ({
                                        /* stroke-neutral-100 在亮色下等同白底，分割线不可见；用主题 divider（grey200）对齐一般分隔线观感 */
                                        borderColor: theme.palette.divider,
                                    })}
                                />
                            }
                            spacing={4}
                        >
                            {cardArr.map((item) => {
                                const rawVal = historyDataStore.data[item.key];
                                const displayVal = item.isPrice
                                    ? formatEUR((rawVal as number) || 0)
                                    : String(rawVal ?? '0');
                                return (
                                    <Box
                                        key={item.key}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            minHeight: kpiRowMinHeight,
                                        }}
                                    >
                                        {/* 图标 + 标签（图标区 24×24，与稿一致） */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {item.icon ? (
                                                <Box
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {item.icon}
                                                </Box>
                                            ) : (
                                                /* 无图标条目用空白占位，保持文字对齐 */
                                                <Box sx={{ width: 24, flexShrink: 0 }} />
                                            )}
                                            <Typography
                                                variant="body1"
                                                sx={{ color: 'var(--text-dark)', fontWeight: 500 }}
                                            >
                                                {item.label}
                                            </Typography>
                                        </Box>

                                        {/* 数值 */}
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: item.isErrorValue
                                                    ? 'var(--func-error)'
                                                    : 'var(--text-dark)',
                                                fontWeight: 500,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {displayVal}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </CardContent>
                </Card>

                {/* 右列：折线图 + 双饼图 */}
                {/* 子项间距 24px：折线区块 与 饼图行 */}
                <Stack
                    spacing={0}
                    sx={{
                        minWidth: 0,
                        minHeight: 0,
                        width: '100%',
                        gap: '24px',
                    }}
                >
                    <Box>
                        {/* 工具栏/图例行 与 折线图 `Card` 的间距 16px */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: '16px',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            {/* 折线图指标选择（对应 Figma "视图选项按钮"） */}
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    bgcolor: 'var(--fill-neutral-100)',
                                    boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.25)',
                                    borderRadius: 1,
                                    px: 2,
                                    py: 0.5,
                                }}
                            >
                                <CommonInput
                                    inputType="SELECT"
                                    select
                                    items={selectList}
                                    value={historyDataStore.selectListValue}
                                    onChange={(e) =>
                                        historyDataStore.updateSelectListValue(
                                            e.target.value as HistoryCartogramMetric
                                        )
                                    }
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                        '& .MuiSelect-select': {
                                            color: 'var(--text-primary-300)',
                                            fontWeight: 700,
                                            py: 0.5,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                        <Card
                            sx={{
                                ...cardSx,
                                minHeight: LINE_CHART_CARD_MIN_HEIGHT_PX,
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: `${LINE_CHART_CARD_CONTENT_PADDING_PX}px`,
                                    '&:last-child': {
                                        pb: `${LINE_CHART_CARD_CONTENT_PADDING_PX}px`,
                                    },
                                }}
                            >
                                <LinechartOfBusinessData
                                    series={historyBusinessLineSeries}
                                    apexOptions={historyBusinessLineApexOptions}
                                    height={LINE_CHART_APEX_HEIGHT}
                                    rootId="line-chart-of-business-data"
                                    rawXLabels={historyDataStore.chartConfig.XAxisText}
                                />
                            </CardContent>
                        </Card>
                    </Box>



                    {/* 双饼图：md+ 一行两卡；xs 一卡一行 */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: '24px',
                            alignItems: 'stretch',
                        }}
                    >
                        {/* 堂食/外卖 营收占比 */}
                        <Card
                            sx={{
                                ...cardSx,
                                flex: { xs: 'none', md: 1 },
                                width: { xs: '100%', md: 'auto' },
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                p: 2,
                            }}
                        >
                            <PieChartOfBusinessData
                                data={[
                                    (historyDataStore.data['eat_in_money_total' as keyof HistoryPeriod] as number) || 0,
                                    (historyDataStore.data['take_out_money_total' as keyof HistoryPeriod] as number) || 0,
                                ]}
                                labels={[
                                    i18n.t('business_analysis_page_text_hall_amount_total'),
                                    i18n.t('business_analysis_page_text_takeout_amount_total'),
                                ]}
                                titleText={i18n.t('business_analysis_page_meal_or_takeout_total_sales_proportion')}
                            />
                        </Card>

                        {/* 支付类型占比 */}
                        <Card
                            sx={{
                                ...cardSx,
                                flex: { xs: 'none', md: 1 },
                                width: { xs: '100%', md: 'auto' },
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                p: 2,
                            }}
                        >
                            <PieChartOfBusinessData
                                data={
                                    historyDataStore?.data.payment_type?.map(
                                        (item) => item.total
                                    ) || []
                                }
                                labels={
                                    historyDataStore?.data.payment_type?.map(
                                        (item) => item.tender_media_name
                                    ) || []
                                }
                                titleText={i18n.t('business_analysis_page_payment_type_total_sales_proportion')}
                            />
                        </Card>
                    </Box>
                </Stack>
            </Box>
        </Stack>
    );
});

/**
 * 历史经营数据页加载骨架：布局与实页面一致（顶栏 | 左 KPI 与右列双列 / alignItems stretch 等高 | 右折线 + 双饼图），
 * 风格参考 group-total-revenue 的 LoadingSkeleton（卡片底+圆角+细分块）
 */
function HistoryDataPageLoadingSkeleton({
    cardSx,
    kpiSkeletonRowCount,
    kpiRowMinHeight,
}: {
    cardSx: SxProps<Theme>;
    kpiSkeletonRowCount: number;
    /** KPI 行最小高度，与实页面一致 */
    kpiRowMinHeight: number;
}) {
    return (
        <Stack spacing={3} sx={{ py: 2 }}>
            {/* 顶栏：标题 + 日期筛选占位 */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Skeleton variant="text" width={220} height={40} />
                <Skeleton variant="rounded" width={320} height={44} sx={{ borderRadius: 1.5 }} />
            </Box>

            {/* 与实页面同：Grid 1fr / 2fr（md+），xs 单列 */}
            <Box
                sx={{
                    display: 'grid',
                    gap: '24px',
                    alignItems: 'stretch',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 2fr)' },
                }}
            >
                {/* 左列：KPI 卡片内多行（图标 + 标签 + 数值） */}
                <Card
                    sx={{
                        ...cardSx,
                        width: '100%',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    <CardContent
                        sx={{
                            px: 2,
                            py: 3,
                            '&:last-child': { pb: 3 },
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Stack
                            divider={
                                <Divider
                                    flexItem
                                    sx={(theme) => ({ borderColor: theme.palette.divider })}
                                />
                            }
                            spacing={4}
                            sx={{ width: '100%' }}
                        >
                            {Array.from({ length: kpiSkeletonRowCount }, (_, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        minHeight: kpiRowMinHeight,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                                        <Skeleton
                                            variant="rounded"
                                            width={24}
                                            height={24}
                                            sx={{ borderRadius: 1, flexShrink: 0 }}
                                        />
                                        <Skeleton
                                            variant="text"
                                            width={140 + (i % 4) * 12}
                                            height={22}
                                        />
                                    </Box>
                                    <Skeleton variant="text" width={72} height={22} sx={{ flexShrink: 0 }} />
                                </Box>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>

                {/* 右列：折线图工具栏 + 图卡 + 双饼图；与实页面同宽填充并与左列等高 */}
                <Stack
                    spacing={0}
                    sx={{
                        minWidth: 0,
                        minHeight: 0,
                        width: '100%',
                        gap: '24px',
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: '16px',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Skeleton variant="rounded" width={200} height={40} sx={{ borderRadius: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Skeleton variant="rounded" width={8} height={2} />
                                    <Skeleton variant="text" width={80} height={20} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Skeleton variant="rounded" width={8} height={2} />
                                    <Skeleton variant="text" width={80} height={20} />
                                </Box>
                            </Box>
                        </Box>
                        {/* 与实页面折线白卡：`minHeight`、CardContent 12px、350 图区骨架 */}
                        <Card
                            sx={{
                                ...cardSx,
                                minHeight: LINE_CHART_CARD_MIN_HEIGHT_PX,
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: `${LINE_CHART_CARD_CONTENT_PADDING_PX}px`,
                                    '&:last-child': {
                                        pb: `${LINE_CHART_CARD_CONTENT_PADDING_PX}px`,
                                    },
                                }}
                            >
                                {/*
                                 * 与 `LinechartOfBusinessData` 一致：Apex `height` 为整图高度（含 title/图例/坐标），不再额外叠一行骨架
                                 */}
                                <Box sx={{ width: '100%' }}>
                                    <Skeleton
                                        variant="rounded"
                                        width="100%"
                                        height={LINE_CHART_APEX_HEIGHT}
                                        sx={{ borderRadius: 1, display: 'block' }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: '24px',
                            alignItems: 'stretch',
                        }}
                    >
                        {[0, 1].map((idx) => (
                            <Card
                                key={idx}
                                sx={{
                                    ...cardSx,
                                    flex: { xs: 'none', md: 1 },
                                    width: { xs: '100%', md: 'auto' },
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: 2,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 0,
                                        '&:last-child': { pb: 2 },
                                    }}
                                >
                                    {/* 与 `PieChartOfBusinessData` 根 `Box` 同 min/max 宽，圆区与饼图默认可视高度一致 */}
                                    <Box
                                        sx={{
                                            minWidth: PIE_CHART_BOX_MIN_W,
                                            maxWidth: PIE_CHART_BOX_MAX_W,
                                            width: '100%',
                                            mx: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack spacing={1.5} alignItems="center">
                                            <Skeleton variant="text" width="72%" height={24} />
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    py: 0.5,
                                                }}
                                            >
                                                <Skeleton
                                                    variant="circular"
                                                    width={PIE_CHART_SKELETON_CIRCLE}
                                                    height={PIE_CHART_SKELETON_CIRCLE}
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1.5,
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Skeleton variant="rounded" width={64} height={28} />
                                                <Skeleton variant="rounded" width={64} height={28} />
                                                <Skeleton variant="rounded" width={64} height={28} />
                                            </Box>
                                        </Stack>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Stack>
            </Box>
        </Stack>
    );
}

export default HistoryDataPage;
